import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faTimes, faRotate } from "@fortawesome/free-solid-svg-icons";
import fetchData from "../api/fetchData";
import Spinner from "../components/Spinner";
import { CATEGORIES, DEFAULT_CATEGORY, categoryName } from "../utils/categories";

/**
 * Category management.
 *
 * A category is a property of a store, so everything here works by setting a
 * store's category — there is no separate membership list to get out of step
 * with the stores themselves. The catalog documents the site reads are updated
 * by the API on each change.
 */
const CategoriesPage = () => {
  const [stores, setStores] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [picker, setPicker] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The store list is the source of truth for membership: counts are
      // derived from it rather than fetched separately, so a count can never
      // disagree with the stores listed beneath it.
      const allStores = await fetchData("/stores/all");
      setStores(allStores || []);
      setNotice(null);
    } catch (error) {
      setNotice({ type: "error", text: error?.message || "Could not load categories." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const storesIn = useCallback(
    (categoryId) =>
      stores
        .filter((s) => (s.category || DEFAULT_CATEGORY) === categoryId)
        .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id))),
    [stores]
  );

  const uncategorised = useMemo(
    () => stores.filter((s) => !s.category).length,
    [stores]
  );

  const setStoreCategory = async (storeId, categoryId, description) => {
    setBusy(true);
    try {
      await fetchData(`/stores/update`, `?id=${encodeURIComponent(storeId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ category: categoryId }),
      });
      // The store list is cached server-side; clear it so the counts we reload
      // reflect the change rather than the previous minute's snapshot.
      await fetchData("/stores/clearStoreCache", null, { method: "POST" }).catch(() => {});
      setNotice({ type: "success", text: description });
      await load();
    } catch (error) {
      setNotice({ type: "error", text: error?.message || "That change could not be saved." });
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async (categoryId) => {
    const storeId = picker.trim();
    if (!storeId) return;

    // The picker only offers real stores, but it accepts typing too — so the
    // id is checked against the loaded stores before anything is sent.
    const store = stores.find((s) => s.id === storeId);
    if (!store) {
      setNotice({
        type: "error",
        text: `No store with the id "${storeId}". Create the store first, then add it to a category.`,
      });
      return;
    }
    if ((store.category || DEFAULT_CATEGORY) === categoryId) {
      setNotice({ type: "error", text: `${store.name || store.id} is already in ${categoryName(categoryId)}.` });
      return;
    }

    const from = categoryName(store.category || DEFAULT_CATEGORY);
    setPicker("");
    await setStoreCategory(
      storeId,
      categoryId,
      `Moved ${store.name || store.id} from ${from} to ${categoryName(categoryId)}.`
    );
  };

  const handleRemove = async (store, categoryId) => {
    if (categoryId === DEFAULT_CATEGORY) {
      setNotice({
        type: "error",
        text: "Extras is where uncategorised stores live — move the store to another category instead.",
      });
      return;
    }
    const confirmed = window.confirm(
      `Remove ${store.name || store.id} from ${categoryName(categoryId)}?\n\n` +
        `A store always belongs to one category, so it moves to Extras.`
    );
    if (!confirmed) return;
    await setStoreCategory(
      store.id,
      DEFAULT_CATEGORY,
      `${store.name || store.id} moved to Extras.`
    );
  };

  const handleRebuild = async () => {
    setBusy(true);
    try {
      const result = await fetchData("/stores/rebuild-catalog", null, { method: "POST" });
      setNotice({ type: "success", text: result?.message || "Category listings rebuilt." });
    } catch (error) {
      setNotice({ type: "error", text: error?.message || "Rebuild failed." });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="p-4 md:p-8 bg-gray-100 min-h-screen w-full">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Categories</h1>
          <p className="text-sm text-gray-600 mt-1">
            {stores.length} stores across {CATEGORIES.length} categories.
            {uncategorised > 0 && ` ${uncategorised} have no category set and are shown under Extras.`}
          </p>
        </div>
        <button
          onClick={handleRebuild}
          disabled={busy}
          title="Regenerate every category listing from the stores. Only needed to repair drift."
          className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 disabled:bg-gray-300"
        >
          <FontAwesomeIcon icon={faRotate} className="mr-2" />
          Rebuild listings
        </button>
      </header>

      {notice && (
        <div
          className={`mb-4 p-3 rounded border text-sm ${
            notice.type === "error"
              ? "bg-red-50 border-red-300 text-red-800"
              : "bg-green-50 border-green-300 text-green-800"
          }`}
        >
          {notice.text}
        </div>
      )}

      <div className="bg-white rounded-lg shadow divide-y">
        {CATEGORIES.map((category) => {
          const inCategory = storesIn(category.id);
          const isOpen = expanded === category.id;
          return (
            <div key={category.id}>
              <button
                onClick={() => {
                  setExpanded(isOpen ? null : category.id);
                  setPicker("");
                }}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 text-left"
              >
                <span className="flex items-center gap-3">
                  <FontAwesomeIcon
                    icon={isOpen ? faChevronDown : faChevronRight}
                    className="text-gray-400 w-3"
                  />
                  <span className="font-semibold text-gray-800">{category.name}</span>
                  <span className="text-xs text-gray-400 font-mono">{category.id}</span>
                </span>
                <span
                  className={`text-sm px-3 py-1 rounded-full ${
                    inCategory.length ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {inCategory.length} {inCategory.length === 1 ? "store" : "stores"}
                </span>
              </button>

              {isOpen && (
                <div className="px-5 pb-5 bg-gray-50">
                  <div className="flex flex-col md:flex-row gap-2 py-4">
                    <input
                      list={`stores-${category.id}`}
                      value={picker}
                      onChange={(e) => setPicker(e.target.value)}
                      placeholder="Add a store by id — pick from the list"
                      className="flex-1 p-2 border border-gray-300 rounded"
                    />
                    <datalist id={`stores-${category.id}`}>
                      {stores
                        .filter((s) => (s.category || DEFAULT_CATEGORY) !== category.id)
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name} — currently {categoryName(s.category || DEFAULT_CATEGORY)}
                          </option>
                        ))}
                    </datalist>
                    <button
                      onClick={() => handleAdd(category.id)}
                      disabled={busy || !picker.trim()}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-300"
                    >
                      Add to {category.name}
                    </button>
                  </div>

                  {inCategory.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2">No stores in this category yet.</p>
                  ) : (
                    <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {inCategory.map((store) => (
                        <li
                          key={store.id}
                          className="flex items-center justify-between bg-white border rounded px-3 py-2"
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-gray-800">{store.name}</span>
                            <span className="block truncate text-xs text-gray-400 font-mono">{store.id}</span>
                          </span>
                          <button
                            onClick={() => handleRemove(store, category.id)}
                            disabled={busy}
                            title="Move this store to Extras"
                            className="text-gray-400 hover:text-red-600 px-2 disabled:opacity-40"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoriesPage;
